import { sentenceCase } from './format.ts';

/**
 * Human-readable names for the Edit tab's visual options.
 *
 * The option schema is derived, not authored: `allowedFor()` in
 * `server/codecs/slide-option-codec.ts` pulls enum values straight out of
 * `primitives.jsx` lookup tables at runtime, so a new size key can appear in
 * the panel the moment a primitive grows one. **Every lookup here therefore
 * falls back to the raw value** — a missing entry must degrade to `md`, never
 * to a blank segment.
 *
 * Keyed by PROP, because the same word means different things per prop:
 * `fill` is a `look`, `medium` is a `scrim`, `md` is a `size`.
 */

/** prop → { rawValue: label }. Values absent here render as themselves —
 *  `width` (20%…75%) and the number steppers already read fine. */
const VALUE_LABELS: Record<string, Record<string, string>> = {
  size: {
    xs: 'Extra small',
    sm: 'Small',
    md: 'Medium',
    lg: 'Large',
    xl: 'Extra large',
    xxl: 'Huge',
  },
  look: {
    keyline: 'Keyline',
    outline: 'Outline',
    edge: 'Edge',
    fill: 'Filled',
  },
  scrim: {
    none: 'None',
    subtle: 'Subtle',
    medium: 'Medium',
    strong: 'Strong',
    gradient: 'Gradient',
  },
  // Two different prop universes share the name: ImageSlide's left/right
  // (which side the photo sits on) and ImageFullSlide's corner positions
  // (where the text block sits over the photo). The keys don't collide, so
  // one table covers both.
  align: {
    left: 'Left',
    right: 'Right',
    'top-left': 'Top left',
    'top-right': 'Top right',
    'bottom-left': 'Bottom left',
    'bottom-right': 'Bottom right',
    center: 'Centre',
  },
  // PhotoPanelsSlide: both keep the words off the photograph, so the labels
  // describe what the reader SEES rather than naming the mechanism.
  ink: {
    scrim: 'Fading shade',
    card: 'Solid black card',
  },
  fit: {
    cover: 'Fill the frame',
    contain: 'Show the whole image',
  },
  // Which part of the photo survives a cover crop — CSS object-position pairs,
  // read out as positions rather than as CSS ("left bottom" → "Bottom left").
  objectPosition: {
    'left top': 'Top left',
    'center top': 'Top centre',
    'right top': 'Top right',
    'left center': 'Left',
    center: 'Centre',
    'right center': 'Right',
    'left bottom': 'Bottom left',
    'center bottom': 'Bottom centre',
    'right bottom': 'Bottom right',
  },
  // HeavyTextCurveSlide's ellipse focus — one axis at a time, and `center`
  // spelled the British way.
  imagePosition: {
    left: 'Left',
    center: 'Centre',
    right: 'Right',
    top: 'Top',
    bottom: 'Bottom',
  },
};

/** `element|prop` → label, falling back to a bare-`prop` entry. Element
 *  keying exists for `align`, which reads as two different questions
 *  depending on the primitive. */
const CONTROL_LABELS: Record<string, string> = {
  mono: 'Black & white',
  scrim: 'Image shading',
  look: 'Style',
  size: 'Size',
  columns: 'Columns',
  align: 'Position',
  controls: 'Video controls',
  showNames: 'Show names',
  chrome: 'Slide frame',

  'ImageSlide|align': 'Image side',
  'VideoTextSlide|align': 'Video side',
  'ImageFullSlide|align': 'Text position',
};

/**
 * One option value → the words on its segment button.
 *
 * @param prop the JSX prop this option writes (`size`, `look`…).
 * @param value the raw value the codec round-trips.
 *
 * Returns a label; an unmapped value is sentence-cased rather than shown raw,
 * so a size key added to a primitive tomorrow reads as "Huge", never as `xxl`.
 * Percentages and numbers pass through untouched.
 */
export function optionValueLabel(prop: string, value: string | number | boolean | null | undefined): string {
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  const table = VALUE_LABELS[prop];
  const key = String(value);
  return (table && table[key]) || sentenceCase(key);
}

/**
 * One option group → the words beside its segments.
 *
 * The server's own `spec.label` wins where it exists (`Image width`,
 * `Video width`, `Image fit`, `Show logo`) — those are already friendly, and
 * they sit next to the values they name. This fills in the rest, which
 * otherwise reach the panel as the bare prop name.
 *
 * @param element the JSX element the option belongs to.
 * @param prop the JSX prop this option writes.
 * @param serverLabel `SlideOption.label` from the API.
 *
 * Returns a label; never blank, never a bare prop name.
 */
export function optionControlLabel(element: string, prop: string, serverLabel?: string | null): string {
  if (serverLabel && serverLabel !== prop) return serverLabel;
  return CONTROL_LABELS[`${element}|${prop}`] || CONTROL_LABELS[prop] || sentenceCase(prop);
}

/** `prop` → the sentence under a SWITCH, per state. Only for options whose
 *  consequence isn't readable from the label: the panel's rule is a sentence
 *  saying what the CURRENT answer means, and most rows (Black & white, RKH
 *  logo) already say it in three words. */
const BOOLEAN_HINTS: Record<string, Record<string, string>> = {
  zoom: {
    true: 'Viewers can click the image to open it full size.',
    false: 'The image is part of the slide — clicking it does nothing.',
  },
};

/**
 * The sentence under a boolean option's switch.
 *
 * @param prop the JSX prop this option writes.
 * @param value the current value (null when instances differ).
 *
 * Returns a hint, or nothing for options that need none.
 */
export function optionBooleanHint(prop: string, value: boolean | null | undefined): string | undefined {
  if (typeof value !== 'boolean') return undefined;
  return BOOLEAN_HINTS[prop]?.[String(value)];
}

/** List-group names that need more than a capital letter — either because the
 *  prop name is jargon (`data` is a chart's points) or because the word is an
 *  acronym `sentenceCase` would flatten. */
const LIST_LABELS: Record<string, string> = {
  data: 'Data points',
  faqs: 'FAQs',
  kpis: 'KPIs',
  qa: 'Q&A',
  ctas: 'CTAs',
};

/**
 * A list group's heading. The server names these after the JSX prop or the
 * element's plural noun (`items`, `notes`, `data`, `cards`, `principles`), so
 * they arrive lowercase and occasionally camelCase from a template's own prop
 * (`keyPoints`). Everything the panel shows as a heading is sentence case.
 *
 * @param label `ListGroup.label` from the API.
 *
 * Returns a heading; never blank.
 */
export function listGroupLabel(label: string | null | undefined): string {
  const raw = String(label || '').trim();
  if (!raw) return raw;
  return LIST_LABELS[raw.toLowerCase()] || sentenceCase(raw);
}
